import TagManager from 'react-gtm-module';
import { readAttributionSnapshot } from '../components/marketing/utils/utm';

/**
 * Google Tag Manager (GTM) / GA4 tracking helpers.
 *
 * Data flow:
 * - Prefer pushing events to `window.dataLayer` when available (most reliable).
 * - Fall back to `react-gtm-module` `TagManager.dataLayer()` in environments where `dataLayer` isn't present.
 * - Fire explicit Google Ads conversion actions through `gtag` only for actions configured in Google Ads.
 */

export const GOOGLE_ADS_ID = process.env.REACT_APP_GOOGLE_ADS_ID || 'AW-10983956220';
export const GOOGLE_ADS_SCHEDULE_SEND_TO =
  process.env.REACT_APP_GOOGLE_ADS_SCHEDULE_SEND_TO || `${GOOGLE_ADS_ID}/I1LvcPDylbUcEPy9x_Uo`;

const IDENTIFYING_MARKETING_KEYS = new Set([
  'email',
  'email_address',
  'mail',
  'student_email',
  'customer_email',
  'phone',
  'phone_number',
  'mobile',
  'mobile_number',
  'customer_phone',
  'first_name',
  'last_name',
  'full_name',
  'name',
  'username',
  'user_name',
  'address',
  'postal_code',
  'postcode',
  'zip',
  'external_id',
  'user_data',
  'enhanced_conversion_data',
]);

export const normalizeMarketingKey = (key) => String(key || '')
  .trim()
  .replace(/([a-z0-9])([A-Z])/g, '$1_$2')
  .toLowerCase()
  .replace(/[\s-]+/g, '_');

export const stripIdentifyingMarketingData = (value) => {
  if (Array.isArray(value)) return value.map(stripIdentifyingMarketingData);
  if (!value || typeof value !== 'object') return value;

  return Object.entries(value).reduce((result, [key, item]) => {
    if (IDENTIFYING_MARKETING_KEYS.has(normalizeMarketingKey(key))) return result;
    result[key] = stripIdentifyingMarketingData(item);
    return result;
  }, {});
};

export const sanitizeMarketingUrl = (value) => {
  const text = String(value || '').trim();
  if (!text) return '';

  try {
    const url = new URL(text, typeof window !== 'undefined' ? window.location.origin : 'https://speakable.online');
    [...url.searchParams.keys()].forEach((key) => {
      if (IDENTIFYING_MARKETING_KEYS.has(normalizeMarketingKey(key))) url.searchParams.delete(key);
    });
    return url.toString();
  } catch (error) {
    return text;
  }
};

const generateEventId = () => `${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;

const withoutEmptyValues = (value = {}) => Object.entries(value).reduce((acc, [key, item]) => {
  if (item === undefined || item === null || item === '') return acc;
  acc[key] = item;
  return acc;
}, {});

const buildAttributionEventData = () => {
  if (typeof window === 'undefined') return {};

  try {
    const snapshot = readAttributionSnapshot(window.location?.search || '');
    const earlyMeta = window.__speakableMetaAttribution || {};

    return withoutEmptyValues({
      fbc: snapshot?.fbc || earlyMeta.fbc,
      fbp: snapshot?.fbp || earlyMeta.fbp,
      fbclid: snapshot?.fbclid || earlyMeta.fbclid,
      event_source_url: sanitizeMarketingUrl(snapshot?.sourceUrl || earlyMeta.event_source_url || window.location.href),
      page_location: sanitizeMarketingUrl(window.location.href),
      page_referrer: sanitizeMarketingUrl(document.referrer || ''),
      ga_client_id: snapshot?.gaClientId,
      ga_session_id: snapshot?.gaSessionId,
      visitor_id: snapshot?.visitorId,
    });
  } catch (e) {
    return withoutEmptyValues({
      event_source_url: typeof window !== 'undefined' ? sanitizeMarketingUrl(window.location?.href) : '',
      page_location: typeof window !== 'undefined' ? sanitizeMarketingUrl(window.location?.href) : '',
      page_referrer: typeof document !== 'undefined' ? sanitizeMarketingUrl(document.referrer) : '',
    });
  }
};

/**
 * Fire a dedicated Google Ads conversion action.
 *
 * GTM/dataLayer remains the canonical funnel stream for GA4 and server-side routing.
 * This helper is intentionally separate because Google Ads now has a native website
 * conversion action for trial bookings that needs the `conversion` gtag event.
 *
 * @param {object} conversionData
 * @param {string} conversionData.sendTo Full Google Ads conversion target, e.g. AW-.../label.
 * @param {number} [conversionData.value]
 * @param {string} [conversionData.currency]
 * @param {string} [conversionData.transactionId]
 * @param {string} [conversionData.eventId]
 * @param {object} [conversionData.userData]
 * @returns {boolean} true when the conversion event was handed to gtag.
 */
export const trackGoogleAdsConversion = ({
  sendTo,
  value = 1.0,
  currency = 'PLN',
  transactionId,
  eventId,
  userData,
  ...parameters
} = {}) => {
  if (typeof window === 'undefined' || !sendTo) return false;
  // Intentionally discard client-side identity data. It must never reach GTM
  // or gtag in clear text from the browser.
  void userData;

  const attribution = buildAttributionEventData();
  const conversionPayload = withoutEmptyValues({
    send_to: sendTo,
    value,
    currency,
    transaction_id: transactionId,
    event_id: eventId,
    ...stripIdentifyingMarketingData(parameters),
  });

  const dataLayerEvent = withoutEmptyValues({
    event: 'google_ads_conversion',
    google_ads_send_to: sendTo,
    ...attribution,
    ...conversionPayload,
  });

  if (window.dataLayer) {
    window.dataLayer.push(dataLayerEvent);
  } else {
    TagManager.dataLayer(dataLayerEvent);
  }

  if (typeof window.gtag !== 'function') return false;

  try {
    window.gtag('event', 'conversion', conversionPayload);
    return true;
  } catch (e) {
    return false;
  }
};

/**
 * Push a generic GTM/GA event with optional parameters.
 *
 * @param {string} eventName
 * @param {object} [parameters]
 */
export const gtmTrackEvent = (eventName, parameters = {}) => {
  const attribution = buildAttributionEventData();
  const safeParameters = stripIdentifyingMarketingData(parameters);
  const explicitEventId = safeParameters.event_id || safeParameters.eventId;
  const dataLayerEvent = {
    event: eventName,
    ...attribution,
    ...(eventName === 'page_view' && !explicitEventId ? { event_id: generateEventId() } : {}),
    ...safeParameters
  };
  
  // Use direct dataLayer push (more reliable)
  if (typeof window !== 'undefined' && window.dataLayer) {
    window.dataLayer.push(dataLayerEvent);
   
  } else {
    // Fallback to TagManager method
    TagManager.dataLayer(dataLayerEvent);
  
  }
  
  // NOTE: Do NOT also call window.gtag() here — GTM already forwards
  // dataLayer events to GA4/Google Ads tags. Calling gtag() directly
  // would duplicate every event.
};

/**
 * Track a booking-related event with normalized booking metadata.
 * @param {string} eventType
 * @param {object} [bookingData]
 */
export const trackBookingEvent = (eventType, bookingData = {}) => {
  gtmTrackEvent('booking_event', {
    event_type: eventType,
    booking_id: bookingData.bookingId,
    teacher_id: bookingData.teacherId,
    student_id: bookingData.studentId,
    lesson_type: bookingData.lessonType,
    package_type: bookingData.packageType
  });
};

/**
 * Track a logical page view.
 * @param {string} pageName
 * @param {string|null} [userId]
 */
export const trackPageView = (pageName, userId = null) => {
  gtmTrackEvent('page_view', {
    page_name: pageName,
    user_id: userId
  });
};

/**
 * Track a generic user action (clicks, interactions) with category/label/value fields.
 * @param {string} action
 * @param {string} category
 * @param {string} [label]
 * @param {string|number} [value]
 */
export const trackUserAction = (action, category, label = '', value = '') => {
  gtmTrackEvent('user_action', {
    action,
    category,
    label,
    value
  });
};

/**
 * Track purchase event for Google Tag Manager
 * @param {Object} transactionData - Transaction data
 * @param {string} transactionData.transaction_id - Unique transaction ID (required)
 * @param {number} transactionData.value - Order total amount (required)
 * @param {string} transactionData.currency - Currency code (e.g., 'PLN', 'EUR', 'USD')
 * @param {string} transactionData.email - Customer email (required for enhanced conversions)
 * @param {string} [transactionData.phone_number] - Customer phone (for enhanced conversions)
 * @param {string} [transactionData.first_name] - Customer first name (for enhanced conversions)
 * @param {string} [transactionData.last_name] - Customer last name (for enhanced conversions)
 * @param {Array} transactionData.items - Optional array of items purchased
 */
export const trackPurchase = (transactionData) => {
  // Validate required fields
  if (!transactionData.transaction_id) {
    console.error('GTM trackPurchase: transaction_id is required');
    return;
  }
  if (typeof transactionData.value !== 'number') {
    console.error('GTM trackPurchase: value must be a number');
    return;
  }
  if (!transactionData.currency) {
    console.error('GTM trackPurchase: currency is required');
    return;
  }
  // Build the data layer object
  const purchaseEvent = {
    event: 'purchase',
    value: transactionData.value,
    transaction_id: transactionData.transaction_id,
    currency: transactionData.currency,
  };

  // Add optional ecommerce structure if items are provided
  if (transactionData.items && transactionData.items.length > 0) {
    purchaseEvent.ecommerce = {
      transaction_id: transactionData.transaction_id,
      value: transactionData.value,
      currency: transactionData.currency,
      items: transactionData.items
    };
  }

  // Push to dataLayer
  if (typeof window !== 'undefined' && window.dataLayer) {
    window.dataLayer.push(purchaseEvent);
  } else {
    console.error('GTM trackPurchase: window.dataLayer is not available');
  }

  // NOTE: Do NOT also call window.gtag('event', 'purchase', ...) here.
  // GTM picks up the dataLayer push and fires GA4/Google Ads tags.
  // Calling gtag() directly would triple-fire the purchase event.
};

/**
 * Executes logic in `trackTrialBooking`.
 * @param {any} trialData Input value.
 * @returns {void} No return value.
 */
export const trackTrialBooking = (trialData) => {
  gtmTrackEvent('trial_booking', {
    teacher_id: trialData.teacherId,
    lesson_date: trialData.lessonDate,
    lesson_time: trialData.lessonTime
  });
};

/**
 * Emit a small test event to verify GTM wiring.
 */
export const testGTM = () => {
  gtmTrackEvent('test_event', {
    test: true,
    timestamp: new Date().toISOString(),
    message: 'GTM test successful'
  });
};
